import {toast} from '@wandb/weave/common/components/elements/Toast';
import {makeDidPropsOrStateChange} from '@wandb/weave/common/util/shouldUpdate';
import produce from 'immer';
import * as _ from 'lodash';
import React from 'react';
import {Redirect} from 'react-router-dom';

import {Tag} from '../../types/graphql';
import * as QueryTypes from '../../util/queryTypes';
import * as Run from '../../util/runs';
import {WBTableRowWithRunSetInfo} from '../../util/tablecols';
import {
  type RunSelectorPropsHook,
  useRunSelectorProps,
} from './hooks/useRunSelectorProps';
import {RunSelectorAnalyticsLocationProvider} from './RunSelectorAnalyticsLocationContext';
import {RunSelectorCell} from './RunSelectorCell';
import {RunSelectorExtraTableActionsLeft} from './RunSelectorExtraTableActionsLeft';
import {RunSelectorExtraTableActionsRight} from './RunSelectorExtraTableActionsRight';
import {RunSelectorExtraTableActionsTop} from './RunSelectorExtraTableActionsTop';
import {RunSelectorFeaturesProvider} from './RunSelectorFeaturesContext';
import {RunSelectorRowHeader} from './RunSelectorRowHeader';
import {RunSelectorRunSetProvider} from './RunSelectorRunSetContext';
import {RunsTableHeader} from './RunsTableHeader';
import type {RunSelectorInnerProps} from './types';
import {getColumnsMemoized, getRowsMemoized, getTagsMemoized} from './util';
import type {WBTableColumn, WBTableProps} from './WBTable/types';
import {WBTable, WBTableComp} from './WBTable/WBTable';

/**
 * RunSelectorInner is WBTable, wrapped with GQL queries for project info,
 * tags, and runs. For runs data, there's a top-level query to populate the
 * table and separate sub-queries for each expanded group (see WBTableRow)
 * and CSV download (see TableExport).
 */
const FIXED_COLUMNS = ['run:displayName'];

type AllRunSelectorProps = RunSelectorInnerProps & RunSelectorPropsHook;
type RunToSaveGroupNotes = {group: string; tags: Tag[]};
type RunToSaveNotes = {
  id: string;
  name: string;
  displayName: string;
  notes: string;
};

interface RunSelectorInnerState {
  tableMessage?: JSX.Element;
  creatingSweep: boolean;
  currentPage: number;
  sortPopupOpen: boolean;
}

class RunSelectorInnerComp extends React.Component<
  AllRunSelectorProps,
  RunSelectorInnerState
> {
  didPropsOrStateChange = makeDidPropsOrStateChange({
    name: 'RunSelector',
    deep: ['runSelectorQuery', 'sort', 'updateGroupRunTags', 'runSet'],
    debug: false,
    verbose: true,
  });
  shouldComponentUpdate(
    nextProps: RunSelectorInnerProps,
    nextState: RunSelectorInnerState
  ): boolean {
    return this.didPropsOrStateChange(
      [this.props, nextProps],
      [this.state, nextState]
    );
  }

  state: RunSelectorInnerState = {
    tableMessage: undefined,
    creatingSweep: false,
    currentPage: 1,
    sortPopupOpen: false,
  };

  tableRef = React.createRef<WBTableComp>();

  renderCell: WBTableColumn['renderCell'] = (
    column,
    row: WBTableRowWithRunSetInfo,
    {
      addFilter,
      toggleExpandedRow,
      recursionDepth,
      isGroup,
      isGroupExpanded,
      loadingChildren,
      isHoveringRow,
    }
  ) => {
    if (
      Run.keysEqual(column.key, {
        section: 'run',
        name: 'displayName',
      })
    ) {
      return (
        <RunSelectorRowHeader
          benchmarkMeta={this.props.benchmarkMeta}
          customRunColorsRef={this.props.customRunColorsRef}
          customRunNamesRef={this.props.customRunNamesRef}
          disableRunLink={this.props.disableRunLinks}
          displayBenchmark={this.props.displayBenchmark}
          displayTempCheckbox={Boolean(isHoveringRow)}
          entityName={this.props.entityName}
          groupSelectionsRef={this.props.runSet.groupSelectionsRef}
          isGroup={isGroup}
          isGroupExpanded={Boolean(isGroupExpanded)}
          isSingleMode={this.props.isSingleMode}
          isTableCollapsed={this.props.isTableCollapsed}
          loadingChildren={loadingChildren}
          onSelectRunName={this.props.onSelectRunName}
          onTempCheckboxClick={this.clearTableMessage}
          projectName={this.props.projectName}
          recursionDepth={recursionDepth}
          row={row}
          runSetRef={this.props.runSetRef}
          selectedCount={this.props.tempSelectedCount}
          selectedRunName={this.props.selectedRunName}
          semanticLegendRef={this.props.semanticLegendRef}
          showArtifactCounts={this.props.showArtifactCounts}
          showLogCounts={this.props.showLogCounts}
          tempSelectionsRef={this.props.tempSelectionsRef}
          onGroupButtonClick={() => {
            if (isGroup) {
              return toggleExpandedRow && toggleExpandedRow(row.__address__);
            }
          }}
          stopRun={id =>
            this.props.stopRun({variables: {id}}).then(this.reload)
          }
          setLaunchDrawerRow={this.props.setLaunchDrawerRow}
          isLaunchActive={this.props.isLaunchActive}
          isLaunchPublicQueueActive={this.props.isLaunchPublicQueueActive}
          truncationType={this.props.truncationType}
        />
      );
    }

    const onSaveRowNotes = (newNotes: string) => {
      const isGroupRun = isGroup && row.group === row.name;
      if (isGroupRun) {
        this.saveRunGroupNotes(row, newNotes);
      } else {
        this.saveRunNotes(row, newNotes);
      }
    };

    return (
      <RunSelectorCell
        addFilter={addFilter}
        column={column}
        entityName={this.props.entityName}
        isGroup={Boolean(isGroup)}
        isHoveringRow={Boolean(isHoveringRow)}
        onSaveRowNotes={onSaveRowNotes}
        projectName={this.props.projectName}
        row={row}
      />
    );
  };

  renderHeader = (displayedRows: Run.RunWithSupportedKeys[]) => {
    const expandTable = () => {
      const {isExpandable, isExpanded, onSetExpanded} = this.props;
      if (isExpandable && !isExpanded && onSetExpanded) {
        onSetExpanded(true);
      }
    };

    return (
      <RunsTableHeader
        isSingleMode={this.props.isSingleMode}
        totalRowCount={this.props.totalRuns}
        displayedRows={displayedRows}
        runSetRef={this.props.runSetRef}
        tempSelectionsRef={this.props.tempSelectionsRef}
        customRunNamesRef={this.props.customRunNamesRef}
        showTempSelection={!this.props.isExpandable || !!this.props.isExpanded}
        onlyShowSelected={this.props.tableSettings.onlyShowSelected}
        onAllItemsTempSelected={() => {
          // Show the "select all runs in project?" prompt.
          // TODO(views): This could maybe be factored out somehow.
          if (this.props.totalRuns > displayedRows.length) {
            const isGrouped = !_.isEmpty(this.props.groupSelections.grouping);
            this.setState({
              tableMessage: (
                <>
                  All <strong>{displayedRows.length}</strong>
                  {isGrouped ? ' groups ' : ' runs '} on this page are selected.{' '}
                  <span
                    className="fake-link"
                    onClick={() => {
                      this.props.tempSelectAll();
                      this.clearTableMessage();
                    }}>
                    Select all
                    {isGrouped ? ' groups ' : ' runs '} in this project
                  </span>
                </>
              ),
            });
          }
        }}
        onAllItemsTempUnselected={this.clearTableMessage}
        onVisibilitySelectNone={this.props.selectNone}
        onVisibilitySelectAll={this.props.selectAll}
        onSetOnlyShowSelected={newSetting => {
          this.props.setTableSettings({
            ...this.props.tableSettings,
            onlyShowSelected: newSetting,
          });
          this.reload();
        }}
        onSelectVisibleRuns={expandTable}
      />
    );
  };

  columns() {
    const {filteredKeys, defaultKeys, tableSettings} = this.props;
    return getColumnsMemoized(
      filteredKeys,
      defaultKeys,
      tableSettings,
      this.renderHeader,
      this.renderCell
    );
  }

  getExtraTableActionsLeft = () => {
    const bulkTagsProject = this.props.bulkEditorProjectTagsQuery.initialLoading
      ? undefined
      : this.props.bulkEditorProjectTagsQuery.data?.project;
    const runSelectorQuery = this.props.runSelectorQuery;
    const project = runSelectorQuery.initialLoading
      ? undefined
      : runSelectorQuery?.project;
    const hasEntityStorageEnabled =
      project?.entity.settings?.storageBucketInfo != null;

    return (
      <RunSelectorExtraTableActionsLeft
        closeLaunchConfigModal={this.props.closeLaunchConfigModal}
        enableFreezeRunset={Boolean(this.props.enableFreezeRunset)}
        enableSetProject={Boolean(this.props.enableSetProject)}
        groupTempSelection={this.props.groupTempSelection}
        hasEntityStorageEnabled={hasEntityStorageEnabled}
        isExpandable={Boolean(this.props.isExpandable)}
        isExpanded={Boolean(this.props.isExpanded)}
        isLaunchActive={this.props.isLaunchActive}
        isLaunchPublicQueueActive={
          this.props.isLaunchPublicQueueActive ?? false
        }
        launchDrawerJobRun={this.props.launchDrawerJobRun}
        onAddTag={tag => this.bulkUpdateTag('add', tag)}
        onCreateSweepWithPriorRuns={this.createSweepWithPriorRuns}
        onDeleteArtifacts={(deleteArtifacts: boolean) => {
          this.deleteRuns(deleteArtifacts);
        }}
        onRemoveTag={tag => this.bulkUpdateTag('remove', tag)}
        onMoveRuns={(entity, proj) => this.moveRuns(entity, proj)}
        onMovedRuns={() => {
          this.props.tempSelectNone();
          this.reload();
        }}
        onCloseSortPopup={() => this.setState({sortPopupOpen: false})}
        onOpenSortPopup={() => this.setState({sortPopupOpen: true})}
        onReload={() => this.reload()}
        recommendedGrouping={this.props.recommendedGrouping}
        resetPagination={() => {
          this.tableRef.current && this.tableRef.current.resetPagination();
        }}
        runSet={this.props.runSet}
        setProject={this.props.setProject}
        sortPopupOpen={this.state.sortPopupOpen}
        tableSettings={this.props.tableSettings}
        tags={getTagsMemoized(bulkTagsProject)}
        tempSelectedCount={this.props.tempSelectedCount}
        tempOutputArtifactsCount={this.props.tempOutputArtifactsCount}
        tempSelectedActiveCount={this.props.tempSelectedActiveCount}
        combinedFilter={this.props.combinedFilter}
      />
    );
  };

  getExtraTableActionsRight = () => {
    return (
      <RunSelectorExtraTableActionsRight
        columns={this.columns()}
        currentPage={this.state.currentPage}
        setTableSettings={this.props.setTableSettings}
        tableSettings={this.props.tableSettings}
        topLevelQueryVariables={this.props.topLevelQueryVariables}
      />
    );
  };

  getExtraTableActionsTop = () => {
    return (
      <RunSelectorExtraTableActionsTop
        addToHistory={this.props.addToHistory}
        enableSetProject={Boolean(this.props.enableSetProject)}
        groupSelections={this.props.groupSelections}
        onSetSearch={(query, isRegex) => {
          if (this.tableRef.current) {
            this.tableRef.current.resetPagination();
          }
          this.props.setSearch(query, isRegex);
        }}
        runSet={this.props.runSet}
        setProject={this.props.setProject}
      />
    );
  };

  saveRunNotes = _.debounce((run: RunToSaveNotes, newNotes: string) => {
    const currentNotes = run.notes;
    if (newNotes !== currentNotes) {
      // Sending undefined values for displayName or notes clears them, so
      // we always need to send both.
      this.props.updateRun(run.id, run.name, {
        displayName: run.displayName,
        notes: newNotes,
      });
    }
  }, 500);

  saveRunGroupNotes = _.debounce(
    (run: RunToSaveGroupNotes, newNotes: string) => {
      const {group: name, tags} = run;
      const {entityName, projectName} = this.props;
      this.props.updateGroupRun({
        variables: {
          projectName,
          entityName,
          name,
          tags: tags.map(t => t.name),
          notes: newNotes,
        },
      });
    },
    500
  );

  updateSort = (updateFn: (sort: QueryTypes.Sort) => void) => {
    const {sort, setSort} = this.props;
    const newSort = produce(sort, updateFn);
    setSort(newSort);
  };

  clearTableMessage = (callback?: () => void) => {
    this.setState({tableMessage: undefined}, () => {
      if (callback) {
        callback();
      }
    });
  };

  bulkUpdateTag(actionType: 'add' | 'remove', tagName: string): void {
    const {entityName, projectName, combinedFilter} = this.props;
    this.props
      .bulkUpdateRunTags({
        entityName,
        projectName,
        filters: JSON.stringify(combinedFilter),
        [actionType === 'add' ? 'addTags' : 'removeTags']: [tagName],
      })
      .then(() => {
        // Refreshes the <BulkTagEditor> menu
        if (
          !this.props.bulkEditorProjectTagsQuery.loading &&
          this.props.bulkEditorProjectTagsQuery.refetch
        ) {
          this.props.bulkEditorProjectTagsQuery.refetch();
        }
      });
  }

  moveRuns(
    destinationEntityName: string,
    destinationProjectName: string
  ): Promise<string> {
    const {entityName, projectName, combinedFilter} = this.props;

    return this.props
      .moveRuns({
        variables: {
          sourceEntityName: entityName,
          sourceProjectName: projectName,
          destinationEntityName,
          destinationProjectName,
          filters: JSON.stringify(combinedFilter),
        },
      })
      .then(response => {
        if (response.data == null || response.data.moveRuns == null) {
          throw new Error('Unexpected response');
        }
        return response.data.moveRuns.task.id;
      });
  }

  deleteRuns(deleteArtifacts: boolean): void {
    const {entityName, projectName, combinedFilter} = this.props;

    this.props
      .deleteRuns({
        variables: {
          entityName,
          projectName,
          filters: JSON.stringify(combinedFilter),
          deleteArtifacts,
        },
      })
      .then(resp => {
        if (resp.data?.deleteRuns?.errors) {
          toast(resp.data.deleteRuns.errors, {type: 'error', autoClose: 2500});
        } else {
          toast('Runs deleted', {type: 'success', autoClose: 2500});
        }
        this.reload();
      })
      .catch(err => {
        let errorMessage = 'Unable to delete runs.';
        if (err?.networkError?.response?.status === 403) {
          errorMessage = 'Unable to delete runs. Invalid permissions.';
        }

        toast(errorMessage, {type: 'error', autoClose: 2500});
      });
  }

  createSweepWithPriorRuns = () => this.setState({creatingSweep: true});

  reload = () => {
    if (
      !this.props.runSelectorQuery.loading &&
      this.props.runSelectorQuery.refetch
    ) {
      this.props.runSelectorQuery.refetch();
    }
    // Refreshes the <BulkTagEditor> menu
    if (
      !this.props.bulkEditorProjectTagsQuery.loading &&
      this.props.bulkEditorProjectTagsQuery.refetch
    ) {
      this.props.bulkEditorProjectTagsQuery.refetch();
    }
  };

  onSetCurrentPage = (currentPage: number) => {
    this.setState({currentPage});
  };

  openSortPopup = () => this.setState({sortPopupOpen: true});

  componentDidUpdate() {
    const {entityName, projectName, setCreateSweepArgs} = this.props;
    const {creatingSweep} = this.state;

    if (creatingSweep) {
      window.analytics?.track('Create Sweep Clicked', {
        entityName,
        projectName,
        location: 'runs table',
      });
      setCreateSweepArgs();
    }
  }

  render() {
    const {
      entityName,
      groupSelections,
      isLoadingRuns,
      isLoadingRunsPolled,
      loadMoreRuns,
      projectName,
      runs,
      runSet,
      runSetRef,
      selectedRunName,
      setGrouping,
      tableSettings,
      topLevelQueryVariables,
      totalRuns,
    } = this.props;
    const {tableMessage, creatingSweep} = this.state;

    if (creatingSweep) {
      return (
        <Redirect to={`/${entityName}/${projectName}/create-sweep`} push />
      );
    }

    const runSelectorQuery = this.props.runSelectorQuery;
    const rows = getRowsMemoized(runs, selectedRunName, runSet);

    const shouldShowTableLoading =
      (runSelectorQuery.initialLoading || isLoadingRuns) &&
      !isLoadingRunsPolled;

    const tableProps: WBTableProps = {
      className:
        'run-selector' +
        (this.props.className ? ` ${this.props.className}` : '') +
        (this.props.isExpandable ? ` expandable` : ''),
      clearTableMessage: this.clearTableMessage,
      columns: this.columns(),
      disabledFeatures: this.props.disabledFeatures,
      expandable: this.props.isExpandable,
      expanded: this.props.isExpanded,
      expandedRowAddresses: groupSelections.expandedRowAddresses,
      filters: this.props.filters,
      fixedColumns: FIXED_COLUMNS,
      grouping: groupSelections.grouping,
      isSingleMode: this.props.isSingleMode,
      loading: shouldShowTableLoading,
      onSetExpanded: this.props.onSetExpanded,
      rows,
      setFilters: this.props.setFilters,
      setGrouping,
      setTableSettings: this.props.setTableSettings,
      showArtifactCounts: this.props.showArtifactCounts,
      showLogCounts: this.props.showLogCounts,
      tableLoadMore: loadMoreRuns,
      tableMessage,
      tableSettings,
      title: this.props.title,
      toggleExpandedRow: this.props.toggleExpandedRow,
      totalRowCount: totalRuns,
      updateSort: this.updateSort,
      updateTruncationType: this.props.setTruncationType,
      truncationType: this.props.truncationType,
      extraTableActionsLeft: this.getExtraTableActionsLeft(),
      extraTableActionsRight: this.getExtraTableActionsRight(),
      extraTableActionsTop: this.getExtraTableActionsTop(),
      onSetCurrentPage: this.onSetCurrentPage,
      openSortPopup: this.openSortPopup,
      topLevelQueryVariables,
      sort: this.props.sort,
    };
    return <WBTable tableRef={this.tableRef} {...tableProps} />;
  }
}

export const RunSelectorInner = (props: RunSelectorInnerProps) => {
  const selectedProps = useRunSelectorProps(props);

  return (
    <RunSelectorFeaturesProvider
      disabledFeatures={props.disabledFeatures}
      disableRunLinks={props.disableRunLinks}
      enableFreezeRunset={props.enableFreezeRunset}
      enableSetProject={props.enableSetProject}>
      <RunSelectorRunSetProvider runSetRef={props.runSetRef}>
        <RunSelectorAnalyticsLocationProvider
          location={props.analyticsLocation}>
          <RunSelectorInnerComp {...props} {...selectedProps} />
        </RunSelectorAnalyticsLocationProvider>
      </RunSelectorRunSetProvider>
    </RunSelectorFeaturesProvider>
  );
};
